import styled, { createGlobalStyle, keyframes } from 'styled-components';

// Animations
const overlayShow = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const contentShow = keyframes`
  from {
    opacity: 0;
    transform: translate(-50%, 0) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translate(-50%, 0) scale(1);
  }
`;

// Global styles for cmdk attributes
export const UniversalSearchGlobalStyles = createGlobalStyle`
  [cmdk-root] {
    z-index: 9999;
  }

  [cmdk-overlay] {
    position: fixed;
    inset: 0;
    background: ${({ theme }) => theme.colors.neutral[20] || '#F7FAFC'};
    z-index: 9998;
    animation: ${overlayShow} 150ms cubic-bezier(0.16, 1, 0.3, 1);
    opacity: 0.8;
  }

  [cmdk-dialog] {
    position: fixed;
    left: 50%;
    top: 20%;
    transform: translate(-50%, 0);
    width: 90%;
    max-width: 600px;
    z-index: 9999;
    background: white;
    border-radius: 4px;
    box-shadow: 0 1px 4px 0 rgba(27, 36, 50, 0.06), 0 2px 32px 0 rgba(27, 36, 50, 0.06);
    animation: ${contentShow} 150ms cubic-bezier(0.16, 1, 0.3, 1);
  }

  [cmdk-input] {
    border: none;
    padding: 0;
    font-size: 14px;
    outline: none;
    background: transparent;
    color: ${({ theme }) => theme.colors.text.main};
    width: 100%;
    font-family: inherit;

    &::placeholder {
      color: ${({ theme }) => theme.colors.text.placeholder};
    }
  }
`;

export const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  max-height: 500px;
`;

export const SearchInputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px;
`;

export const SearchIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.text.placeholder};
  flex-shrink: 0;
`;

export const DialogTitle = styled.h2`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border-width: 0;
`;

export const TabsContainer = styled.div`
  display: flex;
  gap: 16px;
  margin: 8px 16px 0 16px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.subtle};
`;

export const Tab = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 4px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? 500 : 400)};
  color: ${({ theme, $active }) => ($active ? theme.colors.text.dark : theme.colors.text.subtle)};
  border-bottom-color: ${({ theme, $active }) => ($active ? theme.colors.purple[400] : 'transparent')};
  transition: all 0.15s ease;
  margin-bottom: -1px;

  &:hover {
    color: ${({ theme }) => theme.colors.text.dark};
  }
`;

export const TabCount = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 16px;
  padding: 0 4px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  color: ${({ theme }) => theme.colors.text.main};
`;

export const SearchList = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0;
  max-height: 400px;
  flex: 1;
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  height: 48px;
  padding: 0 16px;
  background: ${({ theme }) => theme.colors.neutral[20]};
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  flex-shrink: 0;
  width: 100%;
`;

export const FooterAction = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.main};
`;

export const FooterKey = styled.kbd`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 4px;
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.subtle};
  background: linear-gradient(180deg, #fff 0%, #edf2f7 100%);
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  border-radius: 4px;
`;

export const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  color: ${({ theme }) => theme.colors.text.main};
  font-size: 14px;
`;

export const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 16px;
  color: ${({ theme }) => theme.colors.text.main};
  font-size: 14px;
`;

export const LoadingText = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const SearchGroup = styled.div`
  [cmdk-group-heading] {
    padding: 24px 16px 0 16px;
    font-size: 12px;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.text.subtle};
    text-transform: uppercase;
    letter-spacing: 1px;
  }
`;

export const SearchItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  color: ${({ theme }) => theme.colors.text.dark};

  &[aria-selected='true'] {
    margin: 0 8px;
    padding: 8px;
    background-color: ${({ theme }) => theme.colors.purple[100]};
  }

  &:hover {
    margin: 0 8px;
    padding: 8px;
    background-color: ${({ theme }) => theme.colors.purple[100]};
  }
`;

export const ItemIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: ${(props) => (props.$isContact ? '50%' : '4px')};
  background-color: ${({ theme }) => theme.colors.neutral[20]};
  color: ${({ theme }) => theme.colors.neutral[400]};
`;

export const ItemAvatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: ${(props) => (props.$isContact ? '50%' : '4px')};
  object-fit: cover;
`;

export const ItemContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

export const ItemLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.dark};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ItemDescription = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.main};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
